/**
 * MessagingPanel Component
 * Extracted from Dashboard.js to improve performance and maintainability
 * Handles all direct messaging and group chat functionality
 */
import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, ArrowLeft, Image, Video, X, UserPlus, Edit, Trash2, 
  Phone, Copy, Search, Shield, Camera, MoreVertical, Pencil, Ban,
  Bold, Italic, Strikethrough, CornerDownLeft, Check, AlertCircle, Users as UsersIcon
} from 'lucide-react';
import { RichTextEditor } from '../ui/rich-text-editor';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import EmojiPicker from '../EmojiPicker';

// Helper function to get proper image URL
const getImageUrl = (imagePath, apiBase) => {
  if (!imagePath || imagePath === '' || imagePath === 'null' || imagePath === 'undefined') return null;
  if (imagePath.startsWith('data:') || imagePath.startsWith('http')) {
    return imagePath;
  }
  if (imagePath.startsWith('/api/')) {
    return `${apiBase.replace('/api', '')}${imagePath}`;
  }
  if (imagePath.startsWith('/uploads')) {
    return `${apiBase.replace('/api', '')}${imagePath}`;
  }
  return `${apiBase}/${imagePath}`;
};

// Helper function to render formatted message content (bold, italic, strikethrough)
// Supports both old markdown-style (*bold*, _italic_, ~strikethrough~) and HTML
const formatMessageContent = (content) => {
  if (!content) return content;
  
  // If content already contains HTML tags (from rich text editor), return as-is
  if (/<[^>]+>/.test(content)) {
    return content;
  }
  
  // Otherwise, convert markdown-style formatting for backward compatibility
  let formatted = content.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/_([^_]+)_/g, '<em>$1</em>');
  formatted = formatted.replace(/~([^~]+)~/g, '<del>$1</del>');
  return formatted;
};

const MessagingPanel = memo(({ 
  isOpen, 
  onClose, 
  currentUser, 
  users,
  API,
  getAuthToken 
}) => {
  // Internal state for messaging
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [groups, setGroups] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [messageType, setMessageType] = useState('text');
  const [isFullscreenMessage, setIsFullscreenMessage] = useState(null);
  const [isMobileFullscreenChat, setIsMobileFullscreenChat] = useState(false);
  
  // Group management state
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editGroupName, setEditGroupName] = useState('');
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [memberToAdd, setMemberToAdd] = useState('');
  const [isGroupImageUploading, setIsGroupImageUploading] = useState(false);
  
  // Admin view state
  const [isAdminViewOpen, setIsAdminViewOpen] = useState(false);
  const [adminConversations, setAdminConversations] = useState([]);
  const [adminViewChat, setAdminViewChat] = useState(null);
  const [adminChatMessages, setAdminChatMessages] = useState([]);
  
  // Refs for stable operations
  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const groupImageInputRef = useRef(null);
  const messageCacheRef = useRef({});
  const messageCacheTimestampsRef = useRef({});
  const lastMessageInfoRef = useRef({});
  const selectedChatRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(null);

  // Sync selectedChat to ref for polling
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  // Polling for messages and typing status
  useEffect(() => {
    if (!isOpen) return;
    
    const pollMessages = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const currentChat = selectedChatRef.current;
        
        if (currentChat) {
          const cacheKey = currentChat.isGroup ? `group-${currentChat.groupId}` : `dm-${currentChat.email}`;
          let msgRes;
          
          if (currentChat.isGroup) {
            msgRes = await axios.get(`${API}/groups/${currentChat.groupId}/messages`, config);
          } else if (currentChat.email) {
            msgRes = await axios.get(`${API}/messages/${currentChat.email}`, config);
          }
          
          if (msgRes?.data) {
            const newMessages = msgRes.data;
            const lastInfo = lastMessageInfoRef.current[cacheKey] || { count: 0, lastTimestamp: null };
            const newLastTimestamp = newMessages.length > 0 ? newMessages[newMessages.length - 1]?.created_at : null;
            
            if (newMessages.length !== lastInfo.count || newLastTimestamp !== lastInfo.lastTimestamp) {
              messageCacheRef.current[cacheKey] = newMessages;
              messageCacheTimestampsRef.current[cacheKey] = Date.now();
              lastMessageInfoRef.current[cacheKey] = { count: newMessages.length, lastTimestamp: newLastTimestamp };
              setChatMessages(newMessages);
            }
          }
        }
      } catch (error) {
        console.error('Failed to poll messages:', error);
      }
    };

    const messageInterval = setInterval(pollMessages, 3000);
    return () => clearInterval(messageInterval);
  }, [isOpen, API, getAuthToken]);

  // Fetch conversations on open
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  const fetchConversations = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/messages/conversations`, config);
      setConversations(res.data);
      const groupsRes = await axios.get(`${API}/groups`, config);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const fetchMessages = async (partnerEmail, isGroup = false, groupId = null, forceRefresh = false) => {
    const cacheKey = isGroup ? `group-${groupId}` : `dm-${partnerEmail}`;
    const cacheTime = messageCacheTimestampsRef.current[cacheKey];
    const isCacheFresh = cacheTime && (Date.now() - cacheTime) < 10000;
    
    if (!forceRefresh && messageCacheRef.current[cacheKey] && isCacheFresh) {
      setChatMessages(messageCacheRef.current[cacheKey]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 10);
      setMessagesLoading(false);
      return;
    }
    
    if (messageCacheRef.current[cacheKey]) {
      setChatMessages(messageCacheRef.current[cacheKey]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 10);
    } else {
      setChatMessages([]);
    }
    
    setMessagesLoading(true);
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let res;
      if (isGroup && groupId) {
        res = await axios.get(`${API}/groups/${groupId}/messages`, config);
      } else {
        res = await axios.get(`${API}/messages/${partnerEmail}`, config);
        axios.post(`${API}/messages/${partnerEmail}/mark-all-read`, {}, config).catch(() => {});
      }
      
      messageCacheRef.current[cacheKey] = res.data;
      messageCacheTimestampsRef.current[cacheKey] = Date.now();
      setChatMessages(res.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const sendTypingStatus = async (isTyping) => {
    if (!selectedChat) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/messages/typing`, {
        partner_email: selectedChat.isGroup ? null : selectedChat.email,
        group_id: selectedChat.isGroup ? selectedChat.groupId : null,
        is_typing: isTyping
      }, config);
    } catch (error) {}
  };

  const handleTyping = () => {
    const now = Date.now();
    if (!lastTypingRef.current || now - lastTypingRef.current > 2000) {
      sendTypingStatus(true);
      lastTypingRef.current = now;
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStatus(false);
      lastTypingRef.current = null;
    }, 3000);
  };

  // Typing status polling
  useEffect(() => {
    if (!selectedChat || !isOpen) {
      setTypingUsers([]);
      return;
    }
    
    const fetchTypingStatus = async () => {
      try {
        const token = getAuthToken();
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const endpoint = selectedChat.isGroup 
          ? `${API}/groups/${selectedChat.groupId}/typing`
          : `${API}/messages/typing/${selectedChat.email}`;
        const res = await axios.get(endpoint, config);
        setTypingUsers(res.data.typing_users || []);
      } catch (error) {
        setTypingUsers([]);
      }
    };
    
    fetchTypingStatus();
    const interval = setInterval(fetchTypingStatus, 5000);
    return () => clearInterval(interval);
  }, [selectedChat?.email, selectedChat?.groupId, isOpen, API, getAuthToken]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    // Strip HTML tags for the empty check
    const plainTextContent = newMessage.replace(/<[^>]*>/g, '').trim();
    if (!plainTextContent && !imagePreview) return;
    if (!selectedChat) return;
    if (isSendingMessage) return;
    
    setIsSendingMessage(true);
    
    const messageContent = newMessage.trim() || (imagePreview ? '📷 Image' : '');
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const savedMessage = newMessage;
    const savedImagePreview = imagePreview;
    const savedReplyTo = replyToMessage;
    setNewMessage('');
    setImagePreview(null);
    setMessageType('text');
    setReplyToMessage(null);
    
    const optimisticMessage = {
      id: tempId,
      content: messageContent,
      message_type: savedImagePreview ? 'image' : 'text',
      image_url: savedImagePreview || null,
      sender_email: currentUser?.email,
      sender_name: currentUser?.name,
      created_at: new Date().toISOString(),
      is_sending: true,
      status: 'sending',
      reply_to: savedReplyTo ? {
        id: savedReplyTo.id,
        content: savedReplyTo.content,
        sender_name: savedReplyTo.sender_name
      } : null
    };
    
    setChatMessages(prev => [...prev, optimisticMessage]);
    
    const cacheKey = selectedChat.isGroup ? `group-${selectedChat.groupId}` : `dm-${selectedChat.email}`;
    messageCacheRef.current[cacheKey] = [...(messageCacheRef.current[cacheKey] || []), optimisticMessage];
    messageCacheTimestampsRef.current[cacheKey] = Date.now();
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let imageUrl = null;
      if (savedImagePreview && savedImagePreview.startsWith('data:')) {
        try {
          const response = await fetch(savedImagePreview);
          const blob = await response.blob();
          const formData = new FormData();
          formData.append('file', blob, 'image.jpg');
          
          const uploadResponse = await axios.post(`${API}/upload`, formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          imageUrl = uploadResponse.data.url || uploadResponse.data.file_url;
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          toast.error('Failed to upload image');
          setChatMessages(prev => prev.map(m => 
            m.id === tempId ? { ...m, is_sending: false, status: 'failed' } : m
          ));
          setIsSendingMessage(false);
          return;
        }
      }
      
      const messageData = {
        content: savedMessage || (savedImagePreview ? '📷 Image' : ''),
        message_type: savedImagePreview ? 'image' : 'text',
        image_url: imageUrl,
        reply_to_id: savedReplyTo?.id || null
      };
      
      if (selectedChat.isGroup) {
        messageData.group_id = selectedChat.groupId;
      } else {
        messageData.receiver_email = selectedChat.email;
      }
      
      const messageResponse = await axios.post(`${API}/messages`, messageData, config);
      const sentMessage = messageResponse.data;
      
      setChatMessages(prev => prev.map(m => 
        m.id === tempId ? { ...sentMessage, is_sending: false, status: 'sent' } : m
      ));
      
      messageCacheRef.current[cacheKey] = (messageCacheRef.current[cacheKey] || []).map(m => 
        m.id === tempId ? { ...sentMessage, is_sending: false, status: 'sent' } : m
      );
      
      fetchConversations();
    } catch (error) {
      console.error('Message send error:', error);
      setChatMessages(prev => prev.map(m => 
        m.id === tempId ? { ...m, is_sending: false, status: 'failed' } : m
      ));
      messageCacheRef.current[cacheKey] = (messageCacheRef.current[cacheKey] || []).map(m => 
        m.id === tempId ? { ...m, is_sending: false, status: 'failed' } : m
      );
      setNewMessage(savedMessage);
      setImagePreview(savedImagePreview);
      toast.error('Failed to send message. Tap to retry.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result);
        setMessageType('image');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  const handleEditMessage = (msg) => {
    setEditingMessage(msg);
    setNewMessage(msg.content);
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setNewMessage('');
  };

  const handleSaveEdit = async () => {
    const plainTextContent = newMessage.replace(/<[^>]*>/g, '').trim();
    if (!editingMessage || !plainTextContent) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.patch(`${API}/messages/${editingMessage.id}`, 
        { content: newMessage.trim() }, 
        config
      );
      
      setChatMessages(prev => prev.map(m => 
        m.id === editingMessage.id ? { ...m, content: newMessage.trim(), edited: true } : m
      ));
      
      setEditingMessage(null);
      setNewMessage('');
      toast.success('Message edited');
    } catch (error) {
      console.error('Edit failed:', error);
      toast.error('Failed to edit message');
    }
  };

  const handleDeleteMessage = async (messageId, deleteType) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/messages/${messageId}`, {
        ...config,
        data: { delete_type: deleteType }
      });
      
      if (deleteType === 'for_everyone') {
        setChatMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, deleted_for_all: true, content: 'This message was deleted' } : m
        ));
      } else {
        setChatMessages(prev => prev.filter(m => m.id !== messageId));
      }
      
      toast.success(deleteType === 'for_everyone' ? 'Message deleted for everyone' : 'Message deleted');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete message');
    }
  };

  const retryFailedMessage = async (failedMsg) => {
    setChatMessages(prev => prev.filter(m => m.id !== failedMsg.id));
    setNewMessage(failedMsg.content || '');
    if (failedMsg.image_url) {
      setImagePreview(failedMsg.image_url);
    }
    if (failedMsg.reply_to) {
      setReplyToMessage(failedMsg.reply_to);
    }
  };

  const openChat = (user) => {
    setSelectedChat({
      email: user.email,
      name: user.name,
      phone: user.phone,
      profile_picture: user.profile_picture,
      isGroup: false
    });
    const cacheKey = `dm-${user.email}`;
    if (messageCacheRef.current[cacheKey]) {
      setChatMessages(messageCacheRef.current[cacheKey]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 10);
    }
    fetchMessages(user.email);
  };

  const openGroupChat = (group) => {
    setSelectedChat({
      groupId: group.id,
      name: group.name,
      group_image: group.group_image,
      isGroup: true,
      memberCount: group.member_emails?.length || 0,
      createdBy: group.created_by
    });
    const cacheKey = `group-${group.id}`;
    if (messageCacheRef.current[cacheKey]) {
      setChatMessages(messageCacheRef.current[cacheKey]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 10);
    }
    fetchMessages(null, true, group.id);
  };

  const closeMobileChat = () => {
    setSelectedChat(null);
    setIsMobileFullscreenChat(false);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || selectedGroupMembers.length === 0) {
      toast.error('Please enter a group name and select members');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/groups`, {
        name: newGroupName.trim(),
        member_emails: selectedGroupMembers
      }, config);
      
      toast.success('Group created successfully');
      setIsCreateGroupOpen(false);
      setNewGroupName('');
      setSelectedGroupMembers([]);
      fetchConversations();
    } catch (error) {
      console.error('Create group failed:', error);
      toast.error('Failed to create group');
    }
  };

  const handleEditGroup = async () => {
    if (!selectedChat?.groupId || !editGroupName.trim()) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.patch(`${API}/groups/${selectedChat.groupId}`, {
        name: editGroupName.trim()
      }, config);
      
      setSelectedChat(prev => ({ ...prev, name: editGroupName.trim() }));
      setIsEditGroupOpen(false);
      toast.success('Group updated');
      fetchConversations();
    } catch (error) {
      console.error('Edit group failed:', error);
      toast.error('Failed to update group');
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedChat?.groupId) return;
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/groups/${selectedChat.groupId}`, config);
      
      setSelectedChat(null);
      toast.success('Group deleted');
      fetchConversations();
    } catch (error) {
      console.error('Delete group failed:', error);
      toast.error('Failed to delete group');
    }
  };

  const handleAddMember = async () => {
    if (!selectedChat?.groupId || !memberToAdd) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/groups/${selectedChat.groupId}/members`, {
        email: memberToAdd
      }, config);
      
      setIsAddMemberOpen(false);
      setMemberToAdd('');
      toast.success('Member added');
      fetchConversations();
    } catch (error) {
      console.error('Add member failed:', error);
      toast.error('Failed to add member');
    }
  };

  const handleGroupImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChat?.groupId) return;
    
    setIsGroupImageUploading(true);
    
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageUrl = uploadRes.data.url || uploadRes.data.file_url;
      
      await axios.patch(`${API}/groups/${selectedChat.groupId}`, {
        group_image: imageUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedChat(prev => ({ ...prev, group_image: imageUrl }));
      toast.success('Group image updated');
      fetchConversations();
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to update group image');
    } finally {
      setIsGroupImageUploading(false);
    }
  };

  const handleCreateMeeting = () => {
    const meetingId = `officium-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const meetingLink = `https://meet.jit.si/${meetingId}`;
    
    setNewMessage(meetingLink);
    setMessageType('meeting');
    
    window.open(meetingLink, '_blank');
    toast.info('Meeting created! Opening in new tab...');
  };

  const handleOpenAdminMessages = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/messages/conversations`, config);
      setAdminConversations(res.data);
      setIsAdminViewOpen(true);
    } catch (error) {
      console.error('Failed to fetch admin conversations:', error);
      toast.error('Failed to load conversations');
    }
  };

  const fetchAdminMessages = async (user1, user2) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/messages/between/${user1}/${user2}`, config);
      setAdminChatMessages(res.data);
    } catch (error) {
      console.error('Failed to fetch admin messages:', error);
      toast.error('Failed to load messages');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl w-[95vw] md:w-full h-[90vh] md:h-[700px] p-0 flex flex-col overflow-hidden rounded-2xl [&>button]:hidden z-[100]">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-3 md:p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-base md:text-lg font-bold text-white">Messages</h2>
                <p className="text-[10px] md:text-xs text-white/70 hidden sm:block">Stay connected with your team</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentUser?.is_admin && (
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={handleOpenAdminMessages}
                  className="rounded-full text-[10px] md:text-xs bg-white/20 text-white hover:bg-white/30 border-0 h-8 w-8 md:h-auto md:w-auto md:px-3 p-0 md:p-2"
                >
                  <Shield className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">View All</span>
                </Button>
              )}
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setIsCreateGroupOpen(true)}
                className="rounded-full text-[10px] md:text-xs bg-white/20 text-white hover:bg-white/30 border-0 h-8 w-8 md:h-auto md:w-auto md:px-3 p-0 md:p-2"
              >
                <UserPlus className="h-4 w-4 md:mr-1" />
                <span className="hidden md:inline">New Group</span>
              </Button>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => onClose(false)}
                className="rounded-full bg-white/20 text-white hover:bg-white/30 border-0 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="flex flex-1 overflow-hidden bg-gray-50">
            {/* Conversations List */}
            <div className={`w-full md:w-80 lg:w-96 bg-white border-r flex flex-col ${selectedChat ? 'hidden md:flex' : 'flex'}`}>
              <div className="p-2 md:p-3 border-b bg-[#F0F2F5]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input 
                    type="text" 
                    placeholder="Search conversations..." 
                    className="w-full pl-9 pr-4 py-2 text-sm rounded-full border border-gray-200 bg-white focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] outline-none transition-all"
                  />
                </div>
              </div>
              <ScrollArea className="flex-1 bg-white">
                <div className="p-2 space-y-1">
                  {/* Groups Section */}
                  {groups.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-[#075E54] px-3 py-2 uppercase tracking-wider flex items-center gap-2">
                        <UsersIcon className="h-3 w-3" />
                        Groups ({groups.length})
                      </p>
                      {groups.map((group) => (
                        <div
                          key={group.id}
                          onClick={() => openGroupChat(group)}
                          className={`p-3 cursor-pointer transition-all ${
                            selectedChat?.groupId === group.id 
                              ? 'bg-gray-100' 
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {group.group_image ? (
                              <img 
                                src={getImageUrl(group.group_image, API)}
                                alt={group.name}
                                className="h-12 w-12 rounded-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : null}
                            <div className={`h-12 w-12 rounded-full items-center justify-center text-sm font-bold ${group.group_image ? 'hidden' : 'flex'} bg-emerald-500 text-white`}>
                              {group.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900">{group.name}</p>
                              <p className="text-xs text-gray-500">{group.member_emails?.length || 0} members</p>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-dashed my-3 mx-2 border-gray-200"></div>
                    </>
                  )}
                  
                  <p className="text-xs font-semibold text-[#128C7E] px-3 py-2 uppercase tracking-wider flex items-center gap-2">
                    <MessageCircle className="h-3 w-3" />
                    Team Members
                  </p>
                  
                  {/* Direct Messages */}
                  {users
                    .filter(u => u.email !== currentUser?.email && u.role !== 'candidate')
                    .map(user => ({
                      ...user,
                      lastMessageTime: conversations.find(c => c.partner_email === user.email)?.last_message_time || '1970-01-01'
                    }))
                    .sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime))
                    .map((user) => {
                    const existingConvo = conversations.find(c => c.partner_email === user.email);
                    return (
                      <div
                        key={user.id}
                        onClick={() => openChat(user)}
                        className={`p-3 cursor-pointer transition-all ${
                          selectedChat?.email === user.email && !selectedChat?.isGroup
                            ? 'bg-gray-100' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {user.profile_picture ? (
                            <img 
                              src={getImageUrl(user.profile_picture, API)}
                              alt={user.name}
                              className="h-12 w-12 rounded-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : null}
                          <div className={`h-12 w-12 rounded-full items-center justify-center text-sm font-bold bg-emerald-500 text-white ${user.profile_picture ? 'hidden' : 'flex'}`}>
                            {user.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          </div>
                          {existingConvo?.unread_count > 0 && (
                            <Badge className="bg-emerald-500 text-white text-[10px] h-5 w-5 flex items-center justify-center rounded-full p-0">
                              {existingConvo.unread_count}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${selectedChat ? 'flex' : 'hidden md:flex'} ${isMobileFullscreenChat ? 'fixed inset-0 z-[200] bg-white md:relative md:z-auto' : ''}`}>
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="p-3 md:p-4 bg-white border-b shadow-sm flex items-center justify-between safe-area-top">
                    <div className="flex items-center gap-2 md:gap-3">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="md:hidden rounded-full hover:bg-gray-100 h-8 w-8 text-gray-600"
                        onClick={closeMobileChat}
                        data-testid="close-mobile-chat-btn"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                      {selectedChat.isGroup ? (
                        <div 
                          className="relative cursor-pointer group/avatar"
                          onClick={() => groupImageInputRef.current?.click()}
                          title="Click to change group photo"
                        >
                          {selectedChat.group_image ? (
                            <img 
                              src={getImageUrl(selectedChat.group_image, API)}
                              alt={selectedChat.name}
                              className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover border-2 border-gray-200"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-base md:text-lg">
                              {selectedChat.name?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover/avatar:opacity-100 transition-opacity flex items-center justify-center">
                            {isGroupImageUploading ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Camera className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <input
                            ref={groupImageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleGroupImageUpload}
                            className="hidden"
                          />
                        </div>
                      ) : selectedChat.profile_picture ? (
                        <img 
                          src={getImageUrl(selectedChat.profile_picture, API)}
                          alt={selectedChat.name}
                          className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover border-2 border-gray-200"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-base md:text-lg">
                          {selectedChat.name?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-sm md:text-base text-gray-800">{selectedChat.name}</p>
                        <p className="text-[10px] md:text-xs text-gray-500 flex items-center gap-1">
                          {typingUsers.length > 0 ? (
                            <span className="text-green-600 italic flex items-center gap-1">
                              <span className="flex gap-0.5">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                              </span>
                              {typingUsers.length === 1 
                                ? `${typingUsers[0].name} is typing...`
                                : `${typingUsers.map(u => u.name).join(', ')} are typing...`
                              }
                            </span>
                          ) : selectedChat.isGroup ? (
                            <><UsersIcon className="h-3 w-3" /> {selectedChat.memberCount} members</>
                          ) : (
                            <><span className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></span> Online</>
                          )}
                        </p>
                      </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="flex items-center gap-1 md:gap-2">
                      {selectedChat.isGroup && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setEditGroupName(selectedChat.name);
                              setIsEditGroupOpen(true);
                            }}
                            className="rounded-full text-[10px] md:text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 border-0 h-7 md:h-8 px-2 md:px-3"
                            title="Edit Group"
                          >
                            <Edit className="h-3 w-3 md:h-4 md:w-4 md:mr-1" />
                            <span className="hidden md:inline">Edit</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setIsAddMemberOpen(true)}
                            className="rounded-full text-[10px] md:text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 border-0 h-7 md:h-8 px-2 md:px-3"
                          >
                            <UserPlus className="h-3 w-3 md:h-4 md:w-4 md:mr-1" />
                            <span className="hidden md:inline">Add</span>
                          </Button>
                          {(currentUser?.is_admin || selectedChat.createdBy === currentUser?.email) && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={handleDeleteGroup}
                              className="rounded-full text-[10px] md:text-xs text-white bg-red-500 hover:bg-red-600 border-0 h-7 md:h-8 px-2"
                            >
                              <Trash2 className="h-3 w-3 md:h-4 md:w-4" />
                            </Button>
                          )}
                        </>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          if (selectedChat?.phone) {
                            window.open(`tel:${selectedChat.phone}`, '_self');
                            toast.info(`Calling ${selectedChat.name}...`);
                          } else {
                            toast.error('No phone number available');
                          }
                        }}
                        data-testid="call-btn"
                        className="rounded-full bg-green-500 text-white border-0 hover:bg-green-600 h-7 md:h-8 px-2 md:px-3"
                      >
                        <Phone className="h-3 w-3 md:h-4 md:w-4 md:mr-1" />
                        <span className="hidden md:inline">Call</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleCreateMeeting}
                        data-testid="create-meeting-btn"
                        className="rounded-full bg-blue-500 text-white border-0 hover:bg-blue-600 h-7 md:h-8 px-2 md:px-3"
                      >
                        <Video className="h-3 w-3 md:h-4 md:w-4 md:mr-1" />
                        <span className="hidden md:inline">Meet</span>
                      </Button>
                    </div>
                  </div>
                  
                  {/* Messages */}
                  <ScrollArea className="flex-1 p-3 bg-gradient-to-b from-gray-50 to-gray-100" onClick={() => setSelectedMessageId(null)}>
                    <div className="space-y-2">
                      {chatMessages.map((msg, idx) => {
                        const isMe = msg.sender_email === currentUser?.email;
                        const isSending = msg.is_sending || msg.status === 'sending';
                        const isFailed = msg.status === 'failed';
                        
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-200 group`}
                            style={{ animationDelay: `${Math.min(idx, 5) * 30}ms` }}
                          >
                            <div className="flex items-end gap-1 max-w-[85%]">
                              {!isMe && !isSending && (
                                <button
                                  onClick={() => setReplyToMessage(msg)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-500 hover:text-emerald-600 rounded-full hover:bg-white/80"
                                  title="Reply"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                  </svg>
                                </button>
                              )}
                              
                              <div
                                className={`rounded-2xl px-3 py-2 shadow-sm relative cursor-pointer ${
                                  isMe
                                    ? 'bg-emerald-50 border border-emerald-100 rounded-tr-sm'
                                    : 'bg-white border border-gray-100 rounded-tl-sm'
                                } ${isSending ? 'opacity-70' : ''} ${isFailed ? 'bg-red-50 border border-red-200' : ''} ${selectedMessageId === msg.id ? 'ring-2 ring-emerald-400' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isFailed) {
                                    retryFailedMessage(msg);
                                    return;
                                  }
                                  // Single tap opens fullscreen view
                                  if (!msg.deleted_for_all && !isSending) {
                                    setIsFullscreenMessage(msg);
                                  }
                                }}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Right-click / long-press shows action menu
                                  if (!msg.deleted_for_all && !isSending) {
                                    setSelectedMessageId(selectedMessageId === msg.id ? null : msg.id);
                                  }
                                }}
                              >
                                {isFailed && (
                                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow">
                                    <AlertCircle className="h-3 w-3" />
                                    Tap to retry
                                  </div>
                                )}
                                
                                {selectedMessageId === msg.id && !msg.deleted_for_all && (
                                  <div className="absolute -bottom-14 md:-bottom-12 left-1/2 -translate-x-1/2 flex justify-center z-20">
                                    <div className="flex items-center gap-0.5 md:gap-1 bg-white rounded-full shadow-lg border px-2 md:px-3 py-1.5 md:py-2">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleCopyMessage(msg.content); setSelectedMessageId(null); }}
                                        className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors"
                                        title="Copy"
                                      >
                                        <Copy className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-600" />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setReplyToMessage(msg); setSelectedMessageId(null); }}
                                        className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors"
                                        title="Reply"
                                      >
                                        <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                      </button>
                                      {isMe && msg.message_type === 'text' && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleEditMessage(msg); setSelectedMessageId(null); }}
                                          className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors"
                                          title="Edit"
                                        >
                                          <Pencil className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-600" />
                                        </button>
                                      )}
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id, 'for_me'); setSelectedMessageId(null); }}
                                        className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors"
                                        title="Delete for me"
                                      >
                                        <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-600" />
                                      </button>
                                      {isMe && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id, 'for_everyone'); setSelectedMessageId(null); }}
                                          className="p-1.5 md:p-2 hover:bg-red-100 rounded-full transition-colors"
                                          title="Delete for everyone"
                                        >
                                          <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-red-500" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                {msg.reply_to && (
                                  <div className={`mb-2 px-2 py-1 rounded border-l-4 ${isMe ? 'bg-blue-100/50 border-blue-500' : 'bg-gray-100 border-gray-400'}`}>
                                    <p className="text-[10px] font-semibold text-gray-600">{msg.reply_to.sender_name}</p>
                                    <p className="text-xs text-gray-500 truncate">{msg.reply_to.content}</p>
                                  </div>
                                )}
                                
                                {selectedChat?.isGroup && !isMe && (
                                  <p className="text-xs font-semibold mb-1 text-blue-600">{msg.sender_name}</p>
                                )}
                              
                                {msg.message_type === 'image' && msg.image_url && !msg.media_deleted && (
                                  <img 
                                    src={msg.image_url} 
                                    alt="Shared" 
                                    className="max-w-full rounded-lg mb-2 cursor-pointer shadow-md hover:scale-[1.02] transition-transform"
                                    onClick={() => window.open(msg.image_url, '_blank')}
                                  />
                                )}
                                
                                {msg.message_type === 'image' && msg.media_deleted && (
                                  <div className="flex items-center gap-2 text-sm opacity-70 italic mb-2">
                                    <Image className="h-4 w-4" />
                                    <span>Media expired</span>
                                  </div>
                                )}
                                
                                {msg.message_type === 'meeting' && msg.meeting_link ? (
                                  <a 
                                    href={msg.meeting_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors"
                                  >
                                    <Video className="h-4 w-4" />
                                    <span className="font-medium text-sm">Join Meeting</span>
                                  </a>
                                ) : msg.deleted_for_all ? (
                                  <p className="text-sm text-gray-500 italic pr-14 flex items-center gap-1">
                                    <Ban className="h-4 w-4" />
                                    This message was deleted
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed pr-14" dangerouslySetInnerHTML={{ __html: formatMessageContent(msg.content) }} />
                                )}
                                
                                {/* Time and Status */}
                                <div className="absolute bottom-1 right-2 flex items-center gap-1">
                                  {msg.edited && (
                                    <span className="text-[10px] text-gray-400 italic">edited</span>
                                  )}
                                  <span className="text-[10px] text-gray-500">
                                    {isSending ? '' : format(new Date(msg.created_at), 'HH:mm')}
                                  </span>
                                  {isMe && (
                                    isSending ? (
                                      /* Sending - Clock icon */
                                      <svg className="w-4 h-4 text-gray-400 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                      </svg>
                                    ) : isFailed ? (
                                      /* Failed - Red exclamation */
                                      <svg className="w-4 h-4 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                                      </svg>
                                    ) : msg.read ? (
                                      /* Read - Double blue ticks with tooltip */
                                      <div className="relative group/tick">
                                        <svg className="w-4 h-4 text-blue-500 cursor-pointer" viewBox="0 0 16 15" fill="currentColor">
                                          <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                        </svg>
                                        {msg.read_at && (
                                          <div className="absolute bottom-6 right-0 bg-slate-800 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap opacity-0 group-hover/tick:opacity-100 transition-opacity z-50 pointer-events-none">
                                            Seen {format(new Date(msg.read_at), 'MMM d, HH:mm')}
                                          </div>
                                        )}
                                      </div>
                                    ) : msg.delivered !== false ? (
                                      /* Delivered but not read - Double gray ticks */
                                      <svg className="w-4 h-4 text-gray-400" viewBox="0 0 16 15" fill="currentColor" title="Delivered">
                                        <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                      </svg>
                                    ) : (
                                      /* Sent but not delivered - Single gray tick */
                                      <svg className="w-3 h-3 text-gray-400" viewBox="0 0 16 15" fill="currentColor" title="Sent">
                                        <path d="M10.91 3.316l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                      </svg>
                                    )
                                  )}
                                </div>
                              </div>
                              
                              {isMe && !isSending && !msg.deleted_for_all && (
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => setReplyToMessage(msg)}
                                    className="p-1 text-gray-500 hover:text-emerald-600 rounded-full hover:bg-white/80"
                                    title="Reply"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                    </svg>
                                  </button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-white/80" title="More options">
                                        <MoreVertical className="w-4 h-4" />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                      <DropdownMenuItem onClick={() => handleCopyMessage(msg.content)} className="cursor-pointer">
                                        <Copy className="w-4 h-4 mr-2" />
                                        Copy
                                      </DropdownMenuItem>
                                      {msg.message_type === 'text' && (
                                        <DropdownMenuItem onClick={() => handleEditMessage(msg)} className="cursor-pointer">
                                          <Pencil className="w-4 h-4 mr-2" />
                                          Edit
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem onClick={() => handleDeleteMessage(msg.id, 'for_me')} className="text-gray-700 cursor-pointer">
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete for me
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleDeleteMessage(msg.id, 'for_everyone')} className="text-red-600 cursor-pointer">
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete for everyone
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              )}
                              
                              {!isMe && !isSending && !msg.deleted_for_all && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-white/80" title="More options">
                                      <MoreVertical className="w-4 h-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start" className="w-48">
                                    <DropdownMenuItem onClick={() => handleCopyMessage(msg.content)} className="cursor-pointer">
                                      <Copy className="w-4 h-4 mr-2" />
                                      Copy
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDeleteMessage(msg.id, 'for_me')} className="text-gray-700 cursor-pointer">
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete for me
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="px-3 md:px-4 py-2 md:py-3 border-t bg-gradient-to-r from-emerald-50 to-teal-50">
                      <div className="relative inline-block">
                        <img src={imagePreview} alt="Preview" className="h-20 md:h-24 rounded-xl shadow-md border-2 border-emerald-200" />
                        <button
                          onClick={() => { setImagePreview(null); setMessageType('text'); }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 md:p-1.5 shadow-lg hover:bg-red-600 transition-colors"
                        >
                          <X className="h-2.5 w-2.5 md:h-3 md:w-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Reply Preview */}
                  {replyToMessage && (
                    <div className="px-3 py-2 bg-gray-100 border-t flex items-center gap-2">
                      <div className="flex-1 px-3 py-2 bg-white rounded-lg border-l-4 border-emerald-500">
                        <p className="text-xs font-semibold text-emerald-600">{replyToMessage.sender_name}</p>
                        <p className="text-sm text-gray-600 truncate">{replyToMessage.content}</p>
                      </div>
                      <button
                        onClick={() => setReplyToMessage(null)}
                        className="p-1 text-gray-500 hover:text-red-500 rounded-full hover:bg-red-50"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  )}

                  {/* Message Input */}
                  <form onSubmit={editingMessage ? (e) => { e.preventDefault(); handleSaveEdit(); } : handleSendMessage} className="p-3 bg-gray-50 border-t">
                    {editingMessage && (
                      <div className="flex items-center justify-between mb-2 px-2 py-1 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2 text-blue-600">
                          <Pencil className="h-4 w-4" />
                          <span className="text-sm">Editing message</span>
                        </div>
                        <button type="button" onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1 mb-2">
                      {!editingMessage && (
                        <>
                          <input type="file" ref={imageInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
                          <button type="button" onClick={() => imageInputRef.current?.click()} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Attach image">
                            <Image className="h-4 w-4" />
                          </button>
                          <EmojiPicker onEmojiSelect={(emoji) => setNewMessage(prev => prev + emoji)} disabled={isSendingMessage} />
                        </>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">Select text for formatting options</span>
                    </div>
                    
                    <div className="flex items-end gap-2">
                      <div className="flex-1 message-rich-editor">
                        <RichTextEditor
                          value={newMessage}
                          onChange={(html) => {
                            setNewMessage(html);
                            if (!editingMessage) handleTyping();
                          }}
                          placeholder={editingMessage ? "Edit your message..." : "Type a message..."}
                          minHeight="44px"
                          className="message-composer"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        size="icon" 
                        className={`rounded-full h-10 w-10 shadow-md flex-shrink-0 transition-transform active:scale-95 ${
                          editingMessage ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'
                        }`}
                        disabled={isSendingMessage || (!newMessage.replace(/<[^>]*>/g, '').trim() && !imagePreview)}
                        data-testid="send-message-btn"
                      >
                        {isSendingMessage ? (
                          <div className="h-4 w-4 md:h-5 md:w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : editingMessage ? (
                          <Check className="h-4 w-4 md:h-5 md:w-5" />
                        ) : (
                          <Send className="h-4 w-4 md:h-5 md:w-5" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-[#ECE5DD] p-4">
                  <div className="text-center">
                    <div className="h-16 w-16 md:h-20 md:w-20 rounded-2xl bg-[#25D366] flex items-center justify-center mx-auto mb-4 shadow-xl">
                      <MessageCircle className="h-8 w-8 md:h-10 md:w-10 text-white" />
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-slate-700 mb-2">Start a Conversation</h3>
                    <p className="text-gray-500 text-xs md:text-sm">Select a chat from the list to begin messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Message View */}
      {isFullscreenMessage && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4" onClick={() => setIsFullscreenMessage(null)}>
          <div className="relative max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                  {isFullscreenMessage.sender_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-semibold text-white">{isFullscreenMessage.sender_name || 'Unknown'}</p>
                  <p className="text-xs text-white/70">{format(new Date(isFullscreenMessage.created_at), 'PPpp')}</p>
                </div>
              </div>
              <button onClick={() => setIsFullscreenMessage(null)} className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {isFullscreenMessage.reply_to && (
                <div className="mb-4 px-3 py-2 rounded-lg bg-gray-100 border-l-4 border-emerald-500">
                  <p className="text-xs font-semibold text-gray-600">{isFullscreenMessage.reply_to.sender_name}</p>
                  <p className="text-sm text-gray-500">{isFullscreenMessage.reply_to.content}</p>
                </div>
              )}
              
              {isFullscreenMessage.message_type === 'image' && isFullscreenMessage.image_url && (
                <img src={isFullscreenMessage.image_url} alt="Shared" className="max-w-full rounded-lg mb-4 shadow-md" />
              )}
              
              {isFullscreenMessage.deleted_for_all ? (
                <p className="text-gray-400 italic flex items-center gap-2">
                  <Ban className="h-5 w-5" />
                  This message was deleted
                </p>
              ) : (
                <p className="text-lg text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {isFullscreenMessage.content}
                </p>
              )}
              
              {isFullscreenMessage.edited && (
                <p className="text-xs text-gray-400 italic mt-4">Edited</p>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {!isFullscreenMessage.deleted_for_all && (
                  <>
                    <button onClick={() => { handleCopyMessage(isFullscreenMessage.content); toast.success('Copied!'); }} className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm flex items-center gap-2 transition-colors">
                      <Copy className="h-4 w-4" />
                      Copy
                    </button>
                    <button onClick={() => { setReplyToMessage(isFullscreenMessage); setIsFullscreenMessage(null); }} className="px-3 py-2 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-sm flex items-center gap-2 transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      Reply
                    </button>
                    {isFullscreenMessage.sender_email === currentUser?.email && isFullscreenMessage.message_type === 'text' && (
                      <button onClick={() => { handleEditMessage(isFullscreenMessage); setIsFullscreenMessage(null); }} className="px-3 py-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm flex items-center gap-2 transition-colors">
                        <Pencil className="h-4 w-4" />
                        Edit
                      </button>
                    )}
                    <button onClick={() => { handleDeleteMessage(isFullscreenMessage.id, 'for_me'); setIsFullscreenMessage(null); }} className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm flex items-center gap-2 transition-colors">
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                    {isFullscreenMessage.sender_email === currentUser?.email && (
                      <button onClick={() => { handleDeleteMessage(isFullscreenMessage.id, 'for_everyone'); setIsFullscreenMessage(null); }} className="px-3 py-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 text-sm flex items-center gap-2 transition-colors">
                        <Trash2 className="h-4 w-4" />
                        Delete All
                      </button>
                    )}
                  </>
                )}
              </div>
              <button onClick={() => setIsFullscreenMessage(null)} className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-sm transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Dialog */}
      <Dialog open={isCreateGroupOpen} onOpenChange={setIsCreateGroupOpen}>
        <DialogContent className="max-w-md z-[110]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-500" />
              Create New Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Group Name</Label>
              <Input
                id="group-name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Enter group name"
                className="rounded-sm"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Select Members</Label>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                {users.filter(u => u.email !== currentUser?.email && u.role !== 'candidate').map((user) => (
                  <label key={user.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGroupMembers.includes(user.email)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedGroupMembers([...selectedGroupMembers, user.email]);
                        } else {
                          setSelectedGroupMembers(selectedGroupMembers.filter(m => m !== user.email));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{user.name}</span>
                    <span className="text-xs text-muted-foreground">({user.email})</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{selectedGroupMembers.length} members selected</p>
            </div>
            
            <Button onClick={handleCreateGroup} className="w-full rounded-sm">
              Create Group
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={isEditGroupOpen} onOpenChange={setIsEditGroupOpen}>
        <DialogContent className="max-w-md z-[110]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-500" />
              Edit Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-group-name">Group Name</Label>
              <Input
                id="edit-group-name"
                value={editGroupName}
                onChange={(e) => setEditGroupName(e.target.value)}
                placeholder="Enter group name"
                className="rounded-sm"
              />
            </div>
            <Button onClick={handleEditGroup} className="w-full rounded-sm">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
        <DialogContent className="max-w-md z-[110]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-500" />
              Add Member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Member</Label>
              <select
                value={memberToAdd}
                onChange={(e) => setMemberToAdd(e.target.value)}
                className="w-full p-2 border rounded-lg"
              >
                <option value="">Select a member...</option>
                {users
                  .filter(u => u.email !== currentUser?.email && u.role !== 'candidate')
                  .map((user) => (
                    <option key={user.id} value={user.email}>{user.name} ({user.email})</option>
                  ))}
              </select>
            </div>
            <Button onClick={handleAddMember} className="w-full rounded-sm" disabled={!memberToAdd}>
              Add Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin View All Messages Dialog */}
      <Dialog open={isAdminViewOpen} onOpenChange={setIsAdminViewOpen}>
        <DialogContent className="max-w-4xl h-[650px] p-0 flex flex-col z-[110]">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="font-heading flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-500" />
              Admin: All Conversations
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-1 overflow-hidden">
            {/* Conversations List */}
            <div className={`w-full md:w-1/3 border-r flex flex-col ${adminViewChat ? 'hidden md:flex' : 'flex'}`}>
              <div className="p-3 border-b bg-red-50">
                <p className="text-sm font-medium text-red-700">All User Conversations</p>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {adminConversations.map((convo, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setAdminViewChat(convo);
                        fetchAdminMessages(convo.user1, convo.user2);
                      }}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        adminViewChat?.user1 === convo.user1 && adminViewChat?.user2 === convo.user2
                          ? 'bg-red-50 border border-red-200' 
                          : 'hover:bg-secondary'
                      }`}
                    >
                      <p className="font-medium text-sm">{convo.user1_name} ↔ {convo.user2_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{convo.last_message}</p>
                      <p className="text-xs text-muted-foreground">{convo.message_count} messages</p>
                    </div>
                  ))}
                  {adminConversations.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No conversations found</p>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Chat View */}
            <div className={`flex-1 flex flex-col ${adminViewChat ? 'flex' : 'hidden md:flex'}`}>
              {adminViewChat ? (
                <>
                  <div className="p-3 border-b bg-red-50 flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="md:hidden"
                      onClick={() => setAdminViewChat(null)}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                      <p className="font-medium text-red-800">{adminViewChat.user1_name} ↔ {adminViewChat.user2_name}</p>
                      <p className="text-xs text-red-600">{adminViewChat.message_count} messages</p>
                    </div>
                  </div>
                  
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-3">
                      {adminChatMessages.map((msg) => (
                        <div key={msg.id} className="flex justify-start">
                          <div className="max-w-[80%] rounded-2xl px-4 py-2 bg-gray-100">
                            <p className="text-xs font-medium text-blue-600 mb-1">{msg.sender_name}</p>
                            <p className="text-sm">{msg.content}</p>
                            <p className="text-[10px] text-gray-500 mt-1">
                              {format(new Date(msg.created_at), 'MMM dd, HH:mm')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Shield className="h-12 w-12 mx-auto mb-3 text-red-300" />
                    <p>Select a conversation to view messages</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Styles for message composer rich text editor */}
      <style>{`
        .message-rich-editor [contenteditable] {
          border-radius: 1rem !important;
          background: white;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          padding: 0.625rem 1rem !important;
          min-height: 44px !important;
          max-height: 150px;
          overflow-y: auto;
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .message-rich-editor [contenteditable]:focus {
          outline: none;
          ring: 1px;
          border-color: #34d399;
          box-shadow: 0 0 0 1px #34d399;
        }
        .message-rich-editor [contenteditable]:empty:before {
          color: #9ca3af;
        }
      `}</style>
    </>
  );
});

MessagingPanel.displayName = 'MessagingPanel';

export default MessagingPanel;
